export default function LeaseReviewDueDiligence() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <i className="ri-search-eye-line"></i>
              <span>Due Diligence</span>
            </div>
            
            <h2 className="text-4xl font-bold text-slate-900 mb-6">
              Lease Review and Buyer Due Diligence Process
            </h2>
            
            <p className="text-lg text-slate-600 mb-6 leading-relaxed">
              Buyers will conduct exhaustive lease reviews during due diligence. Understanding what they're looking for and preparing accordingly can prevent delays, renegotiations, or deal failures.
            </p>

            <h3 className="text-2xl font-bold text-slate-900 mb-4">What Buyers Examine in Lease Documents</h3>

            <div className="space-y-4 mb-8">
              <div className="bg-slate-50 p-5 rounded-xl">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-file-text-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Lease Term Consistency</h4>
                    <p className="text-slate-600 text-sm">
                      Buyers verify that lease terms match what's stated in the rent roll and purchase agreement. Any discrepancies raise red flags about accuracy and management quality.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 p-5 rounded-xl">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-money-dollar-circle-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Rent Amount Verification</h4>
                    <p className="text-slate-600 text-sm mb-2">
                      Every lease is checked against the rent roll to confirm stated rent matches actual lease agreements. Buyers also look for undisclosed concessions or side agreements.
                    </p>
                    <div className="bg-white p-3 rounded-lg">
                      <p className="text-xs text-slate-600">
                        <strong>Common Issue:</strong> Verbal agreements for reduced rent not documented in lease
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 p-5 rounded-xl">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Landlord Obligations</h4>
                    <p className="text-slate-600 text-sm">
                      Buyers scrutinize any landlord obligations like promised repairs, upgrades, or improvements. Unfulfilled obligations become buyer responsibilities or negotiation points.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 p-5 rounded-xl">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-alert-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Unusual or Non-Standard Provisions</h4>
                    <p className="text-slate-600 text-sm mb-2">
                      Special clauses, tenant options, or unique terms are carefully reviewed. These can include purchase options, early termination rights, or rent caps.
                    </p>
                    <div className="bg-amber-50 p-3 rounded-lg">
                      <p className="text-xs text-amber-800">
                        <strong>Warning:</strong> Tenant purchase options or right of first refusal can complicate or prevent sales
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 p-5 rounded-xl">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-calendar-check-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Renewal Options and Terms</h4>
                    <p className="text-slate-600 text-sm">
                      Automatic renewal clauses, tenant renewal options, and rent increase limitations are examined to understand future income potential and flexibility.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 p-5 rounded-xl">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-tools-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Maintenance Responsibilities</h4>
                    <p className="text-slate-600 text-sm">
                      Clear delineation of landlord vs. tenant maintenance obligations affects operating expenses and property condition expectations.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
              <div className="flex items-start gap-3">
                <i className="ri-lightbulb-line text-blue-600 text-2xl mt-1"></i>
                <div>
                  <h4 className="font-semibold text-blue-900 mb-2">Pro Tip: Pre-Due Diligence Review</h4>
                  <p className="text-blue-800 text-sm leading-relaxed">
                    Conduct your own lease audit before listing. Hire a property management consultant or attorney to review all leases and identify potential issues. Addressing problems proactively prevents surprises during buyer due diligence.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://public.readdy.ai/ai/img_res/8494335b1b649e3a48269095fb025222.jpg"
                alt="Lease review and due diligence process for apartment building sale"
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl border border-slate-100 max-w-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                  <i className="ri-time-line text-teal-600 text-2xl"></i>
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-900">14-30</div>
                  <div className="text-sm text-slate-600">Days Average</div>
                </div>
              </div>
              <p className="text-xs text-slate-600">
                Typical lease review period during buyer due diligence
              </p>
            </div>
          </div>
        </div>

        <div className="mt-16 bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-slate-900 mb-6">Preparing Your Lease Files for Due Diligence</h3>
          
          <p className="text-slate-700 mb-6 leading-relaxed">
            Organization and completeness of your lease files directly impacts buyer confidence and deal momentum. A well-organized due diligence package demonstrates professionalism and reduces buyer concerns.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <i className="ri-folder-line text-teal-600 text-xl"></i>
                Essential Documents per Unit
              </h4>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Original signed lease agreement</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>All amendments and addendums</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Tenant application and screening results</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Move-in inspection report with photos</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Security deposit receipt and ledger</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Payment history (12 months minimum)</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Correspondence file (notices, requests, complaints)</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Maintenance request history</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Any violation notices or cure letters</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Pet agreements or parking addendums</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <i className="ri-file-list-3-line text-teal-600 text-xl"></i>
                Property-Level Documents
              </h4>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Current rent roll (updated within 7 days)</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Lease abstract summary for all units</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Security deposit reconciliation</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Tenant estoppel certificates</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Standard lease form template</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Property rules and regulations</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Move-in/move-out procedures</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Tenant handbook or welcome packet</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Rent collection policies and procedures</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Eviction history (past 3 years)</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-12 bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
          <h3 className="text-2xl font-bold text-slate-900 mb-6">Common Due Diligence Issues and How to Address Them</h3>
          
          <div className="space-y-6">
            <div className="border-l-4 border-red-500 pl-6">
              <h4 className="font-semibold text-slate-900 mb-2">Issue: Missing Lease Documents</h4>
              <p className="text-slate-600 text-sm mb-3">
                Some original leases cannot be located, or only copies exist without original signatures.
              </p>
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-xs font-semibold text-teal-600 mb-2">Resolution Strategy:</p>
                <ul className="text-xs text-slate-600 space-y-1">
                  <li>• Have tenants sign lease confirmation agreements acknowledging current terms</li>
                  <li>• Provide affidavit from seller certifying lease terms</li>
                  <li>• Offer price reduction or escrow holdback for affected units</li>
                  <li>• Consider requiring new leases with buyer as landlord post-closing</li>
                </ul>
              </div>
            </div>

            <div className="border-l-4 border-amber-500 pl-6">
              <h4 className="font-semibold text-slate-900 mb-2">Issue: Inconsistent Lease Terms</h4>
              <p className="text-slate-600 text-sm mb-3">
                Different units have wildly different lease provisions, making management complex for buyer.
              </p>
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-xs font-semibold text-teal-600 mb-2">Resolution Strategy:</p>
                <ul className="text-xs text-slate-600 space-y-1">
                  <li>• Create detailed lease abstract highlighting all variations</li>
                  <li>• Explain historical reasons for different terms</li>
                  <li>• Offer to standardize leases at renewal for buyer</li>
                  <li>• Provide transition plan for normalizing terms over time</li>
                </ul>
              </div>
            </div>

            <div className="border-l-4 border-blue-500 pl-6">
              <h4 className="font-semibold text-slate-900 mb-2">Issue: Undisclosed Verbal Agreements</h4>
              <p className="text-slate-600 text-sm mb-3">
                Tenants claim verbal agreements for services, rent reductions, or special arrangements not in written lease.
              </p>
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-xs font-semibold text-teal-600 mb-2">Resolution Strategy:</p>
                <ul className="text-xs text-slate-600 space-y-1">
                  <li>• Document all verbal agreements in writing immediately</li>
                  <li>• Have tenants sign acknowledgment of all arrangements</li>
                  <li>• Adjust rent roll to reflect true effective rent</li>
                  <li>• Renegotiate purchase price if material impact on NOI</li>
                </ul>
              </div>
            </div>

            <div className="border-l-4 border-purple-500 pl-6">
              <h4 className="font-semibold text-slate-900 mb-2">Issue: Unfulfilled Landlord Obligations</h4>
              <p className="text-slate-600 text-sm mb-3">
                Leases contain promises for repairs, upgrades, or improvements that haven't been completed.
              </p>
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-xs font-semibold text-teal-600 mb-2">Resolution Strategy:</p>
                <ul className="text-xs text-slate-600 space-y-1">
                  <li>• Complete all promised work before closing</li>
                  <li>• Negotiate credit to buyer for incomplete items</li>
                  <li>• Escrow funds to cover completion costs</li>
                  <li>• Amend leases to remove unfulfilled obligations (with tenant consent)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
